import { useSelector, useDispatch } from 'react-redux';
import { isEqual } from 'underscore';
import { useState } from 'react';
import {
  useOnce,
  useAsyncEffect,
} from '../../../hooks';
import { ShareTarget } from '../../../modules/share';
import { requireLogin } from '../../shared/login/SignUpLoginModal';

import { IRootDispatch, RootState } from '../../../store/models';
import {
  transformRouteParamsWithShareTarget,
  sendLoggedInReferralCreationRequest,
} from '../../../modules/referrals';
import useApplyDiscountCode from '../../shared/ApplyDiscount/hook';
import nonCriticalException from '../../../modules/exceptionLogger';
import { ReferralLandingLoginHeader } from './ReferralLandingLoginHeader';
import { AUTH_ORIGINS } from '../../../modules/user/constants';
/**
 * Checks if given route param data is valid for attempting
 recipient referral creation
  If user is not logged in, they will be prompted to do so through
  the requireLogin flow

  If/after a user is logged in, valid route param referral data will be
  used in an attempt to create a referral.
  Upon success, the referral info will be applied to discounts

 * @param target The referral target to check
 * @param onTrigger Called when the user is marked as a referral recipient for the given target
 * @returns If there was a client referral creation or not
 */

export function useReferralLanding(
  payload: {
    target: ShareTarget,
    providerId?: number,
  },
  onTrigger: () => void,
): boolean {
  const routeParams = useSelector<RootState, Record<string, any>>(
    state => state.route.params, isEqual,
  );
  const [userLoading, setUserLoading] = useState<boolean>(true);
  const currentUser = useSelector<RootState, RootState['user']>(
    state => state.user,
    (a, b) => a.userId === b.userId,
  );
  const isLoggedInUser = !currentUser.is_anon && !!currentUser.userId;
  const dispatch = useDispatch<IRootDispatch>();
  const clearRouteParams = () => {
    dispatch.route.addParams({
      params: {
        referral_id: '',
        user_id_referrer: '',
        utm_campaign: '',
        utm_medium: '',
        utm_term: '',
      },
      replace: true,
      clearOld: true,
    });
  };

  const detectedValidReferralPayload = transformRouteParamsWithShareTarget(
    routeParams, payload.target,
  );
  useAsyncEffect(async () => {
    // Addressing Possible Race Condition: When the Client lands back on our
    // app, the app is reloaded, and if the current user (whoami) isn’t completed
    // by the time the valid referral parameters are detected, the 'sign in/sign up'
    // modal pops up because that is the intended guest user behavior
    try {
      /* Only make this call when we come back from Klarna */
      if (detectedValidReferralPayload) {
        await dispatch.user.whoami({});
      }
    } finally {
      setUserLoading(false);
    }
  }, [dispatch, detectedValidReferralPayload]);
  const setAppliedDiscounts = useApplyDiscountCode();
  const applyDiscounts = async (source: string,
    code: string, rewardId: number) => setAppliedDiscounts(
    source, code, rewardId,
  );
  const [successfulClientReferralCreation, setSuccessfulClientReferralCreation] = useState(false);
  const attemptApplyReferralParamInfoAndDisplayTrigger = async (
  ) => {
    const referralCreationSuccessful = await sendLoggedInReferralCreationRequest(
      detectedValidReferralPayload,
    );
    if (referralCreationSuccessful) {
      try {
        const { source, code, rewardId } = referralCreationSuccessful;
        await applyDiscounts(source, code, rewardId);
      } catch (e) {
        /* Frontend discount display may throw  'promo invalid' message because
        `applyDiscounts` involves adding the source/code/rewardId to state manager
        and first needs to check it does not already exist. */
        nonCriticalException(
          'useReferralsLanding: race condition',
          { ...(e?.msg && { message: e.msg }) },
        );
      }
      setSuccessfulClientReferralCreation(true);
      onTrigger();
    }
  };

  useOnce(async () => {
    clearRouteParams();
    try {
      await requireLogin(
        'client_login',
        AUTH_ORIGINS.REFERRAL_LANDING,
        null,
        ReferralLandingLoginHeader,
        true,
      ).then(attemptApplyReferralParamInfoAndDisplayTrigger);
    } catch (e) {
      nonCriticalException(
        'useReferralsLanding: Anon user cancelled login',
        { ...(e?.msg && { message: e.msg }) },
      );
    }
  }, (!isLoggedInUser && !userLoading && detectedValidReferralPayload) || null);

  useOnce(async () => {
    clearRouteParams();
    attemptApplyReferralParamInfoAndDisplayTrigger();
  }, (isLoggedInUser && !userLoading && detectedValidReferralPayload) || null);
  return !!successfulClientReferralCreation;
}
